import { ArrowDown, ArrowUp } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
  LIST_SORTS,
  type ListSort,
  type ListSortDir,
} from '@/lib/useListSort'
import { cn } from '@/lib/utils'

type SortDropdownProps = {
  value: ListSort
  dir: ListSortDir
  onChange: (next: ListSort) => void
  onToggleDir: () => void
  className?: string
  // Tightly typed to the i18n namespaces that today carry the
  // sort.option labels. Each page passes its own namespace so we can
  // tune option phrasing per entity (e.g. "Last visit" might phrase
  // better than "Last activity" on Customers later) without forcing a
  // shared common-namespace string.
  namespace: 'customers' | 'vehicles'
}

// Reusable sort selector. Native <select> for the field (matches the
// EricActivityTable pattern and the established Tailwind v3
// conventions) plus a small icon button on the left that flips the
// direction (asc/desc). The button shows the *current* direction so
// the icon doubles as state indicator.
export function SortDropdown({
  value,
  dir,
  onChange,
  onToggleDir,
  className,
  namespace,
}: SortDropdownProps) {
  const { t } = useTranslation(namespace)
  const { t: tCommon } = useTranslation('common')

  const Icon = dir === 'asc' ? ArrowUp : ArrowDown
  const toggleLabel = tCommon(
    dir === 'asc' ? 'sort.toggle.to_desc' : 'sort.toggle.to_asc',
  )

  return (
    <div
      className={cn(
        'flex h-8 w-full items-center rounded-lg border border-input bg-transparent pr-2 text-sm text-slate-700 sm:w-auto',
        className,
      )}
    >
      <button
        type="button"
        onClick={onToggleDir}
        aria-label={toggleLabel}
        title={toggleLabel}
        className="flex h-full items-center justify-center rounded-l-lg px-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700"
      >
        <Icon className="size-3.5" aria-hidden="true" />
      </button>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as ListSort)}
        className="min-w-0 flex-1 cursor-pointer bg-transparent pl-0.5 pr-0 text-sm outline-none"
        aria-label={tCommon('sort.label')}
      >
        {LIST_SORTS.map((key) => (
          <option key={key} value={key}>
            {t(`sort.option.${key}`)}
          </option>
        ))}
      </select>
    </div>
  )
}
